/**
 * Folds a keyword or tag into the form the keyword index is keyed on: lowercase, single spaces, and
 * hyphens/underscores treated as word separators so the data's `"t-shirt"` and `"t shirt"` — both of
 * which appear — collapse onto one key.
 */
export const normalizeKeyword = (value: string): string =>
  value.toLowerCase().normalize('NFKD').replace(/[-_]/g, ' ').replace(/\s+/g, ' ').trim();

export type TagKeywordMatch = {
  /** The normalized product keyword the tag was matched by. */
  keyword: string;
  /** The tag's remaining words, in their original order; `undefined` when the keyword was the whole tag. */
  residualTerm?: string;
};

/** How a phrase's words are classified. Two questions, because they are genuinely different. */
export type ProductWordIndex = {
  /**
   * Can this be *matched on*? The guarded keyword→category map: a keyword resolving to no useful
   * category is absent, which is what lets `"hoodie damen"` fall through the too-broad `damen`.
   */
  isCategoryKeyword: (keyword: string) => boolean;
  /**
   * Is this a product word *at all*? The whole keyword index, before the category guard. Only these
   * may be skipped over on the way leftwards.
   */
  isProductWord: (keyword: string) => boolean;
};

/**
 * Splits a tag into the product keyword it names and the rest of the words, e.g.
 * `"dog hoodie"` → `{ keyword: 'hoodie', residualTerm: 'dog' }`.
 *
 * Longer keywords win, so `"zip hoodie"` beats `"hoodie"`. Within one length the **rightmost** match
 * wins: in both German and English the product kind is the head noun at the end of the phrase, and
 * the modifier — the part the user actually wants to search for — comes first. Scanning left to
 * right would resolve `"dog hoodie"` to pet products, since `dog` is a keyword in its own right.
 *
 * The search may only reach leftwards **across product words**. `"hoodie damen"` resolves through
 * `hoodie` because `damen` is a product word that merely failed the category guard; `"star wars"`
 * resolves to nothing, because `wars` is no product word at all and `star` — a real keyword, two
 * product types, T-Shirts & Tops — is therefore not the head noun of anything. Without that
 * restriction the phrase became a t-shirt search for "wars", quietly dropping half the title.
 */
export const matchTagKeyword = (
  tag: string,
  { isCategoryKeyword, isProductWord }: ProductWordIndex
): TagKeywordMatch | undefined => {
  const tokens = normalizeKeyword(tag).split(' ').filter(Boolean);

  for (let size = tokens.length; size >= 1; size--) {
    for (let start = tokens.length - size; start >= 0; start--) {
      const keyword = tokens.slice(start, start + size).join(' ');

      if (!isCategoryKeyword(keyword)) {
        continue;
      }

      // Everything after the match has to be a product word, or this is not a phrase ending in a
      // product kind.
      if (!tokens.slice(start + size).every(isProductWord)) {
        continue;
      }

      const residual = [...tokens.slice(0, start), ...tokens.slice(start + size)].join(' ');
      return { keyword, residualTerm: residual || undefined };
    }
  }

  return undefined;
};
